import type { OrderNotificationType, OrderStatus } from '@ecom/contracts';

/**
 * The order state machine, as the UI understands it.
 *
 * This mirrors the API's rules so the screen can offer only the moves that are
 * plausible — it is **not** the enforcement point. The API refuses illegal
 * transitions regardless, and its refusal is what the operator sees if these
 * two ever disagree (07-Admin rule 5: UI hiding is not security).
 */

export const ORDER_STATUSES: OrderStatus[] = [
  'PENDING_PAYMENT',
  'AWAITING_COLLECTION',
  'PAID',
  'PROCESSING',
  'SHIPPED',
  'READY_FOR_PICKUP',
  'DELIVERED',
  'CANCELLED',
  'REFUNDED',
];

/**
 * What the screen offers — a deliberate **subset** of what the API permits.
 *
 * The API also allows a direct move to `REFUNDED` from PAID, PROCESSING,
 * SHIPPED, and DELIVERED. That move is not offered here on purpose: it changes
 * the order's status **without moving any money**, so an operator who clicked
 * it would believe a customer had been refunded when nothing had left the
 * account. Refunds belong on the Returns screen, where they go through
 * `PAYMENT_PROVIDER` and are owner-only.
 *
 * `order-status.spec.ts` asserts this stays a subset — if the API ever narrows
 * its state machine, the test fails rather than the operator meeting a refusal.
 */
const OFFERED_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  // Manual confirmation of a payment taken outside the PSP, e.g. a transfer.
  PENDING_PAYMENT: ['PAID', 'CANCELLED'],
  // A small store often ships straight from confirmed, skipping "processing".
  AWAITING_COLLECTION: ['PROCESSING', 'SHIPPED', 'READY_FOR_PICKUP', 'CANCELLED'],
  PAID: ['PROCESSING', 'SHIPPED', 'READY_FOR_PICKUP', 'CANCELLED'],
  PROCESSING: ['SHIPPED', 'READY_FOR_PICKUP', 'CANCELLED'],
  SHIPPED: ['DELIVERED'],
  // Waiting at the showroom (R6): collected, or never collected and cancelled.
  READY_FOR_PICKUP: ['DELIVERED', 'CANCELLED'],
  DELIVERED: [],
  // Terminal: money has moved and stock has been settled.
  CANCELLED: [],
  REFUNDED: [],
};

/**
 * The moves on offer for this order. A pickup is made ready, never shipped, and
 * a delivery never waits to be collected (R6) — the API refuses the other one.
 */
export function nextStatuses(current: OrderStatus, isPickup = false): OrderStatus[] {
  const wrongRoad: OrderStatus = isPickup ? 'SHIPPED' : 'READY_FOR_PICKUP';
  return (OFFERED_TRANSITIONS[current] ?? []).filter((status) => status !== wrongRoad);
}

/** Shipping is the one transition that carries extra required detail. */
export function requiresTracking(target: OrderStatus): boolean {
  return target === 'SHIPPED';
}

/**
 * When the change asks the person to say why. Cancelling always does; so does
 * a money move the API only takes by hand with a reason (its manualMoveRequires):
 * marking a card order paid, and recording any refund.
 */
export function requiresReason(target: OrderStatus, from?: OrderStatus): boolean {
  return target === 'CANCELLED' || target === 'REFUNDED' || (target === 'PAID' && from === 'PENDING_PAYMENT');
}

export function statusTone(status: OrderStatus): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'PENDING_PAYMENT':
      return 'warning';
    case 'PAID':
    case 'AWAITING_COLLECTION':
    case 'READY_FOR_PICKUP':
      return 'info';
    case 'PROCESSING':
    case 'SHIPPED':
      return 'neutral';
    case 'DELIVERED':
      return 'success';
    case 'CANCELLED':
    case 'REFUNDED':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** The action queue: orders that are paid for and waiting on us. */
export function isAwaitingFulfilment(status: OrderStatus): boolean {
  return status === 'PAID' || status === 'AWAITING_COLLECTION' || status === 'PROCESSING';
}

/**
 * The orders list's views — tabs over the status filter, the way the reference
 * dashboards open on "All · Unfulfilled · Unpaid". Each is just a set of
 * statuses, so a view is a filter someone can still see and change.
 */
export const ORDER_VIEWS = [
  { id: 'all', statuses: [] },
  { id: 'toFulfil', statuses: ['PAID', 'AWAITING_COLLECTION', 'PROCESSING'] },
  { id: 'unpaid', statuses: ['PENDING_PAYMENT'] },
  { id: 'onTheWay', statuses: ['SHIPPED', 'READY_FOR_PICKUP'] },
  { id: 'delivered', statuses: ['DELIVERED'] },
  { id: 'closed', statuses: ['CANCELLED', 'REFUNDED'] },
] as const satisfies readonly { id: string; statuses: readonly OrderStatus[] }[];

export type OrderViewId = (typeof ORDER_VIEWS)[number]['id'];

/** The view a status filter is exactly, or null when it is a hand-made mix. */
export function viewOf(statuses: readonly OrderStatus[]): OrderViewId | null {
  const chosen = [...statuses].sort().join();
  return ORDER_VIEWS.find((view) => [...view.statuses].sort().join() === chosen)?.id ?? null;
}

/**
 * The moves a status has a customer message for — mirrors the API's
 * STATUS_NOTIFICATIONS, so the screen only asks "tell the customer?" when
 * there is something to tell them. Moving to processing sends nothing either
 * way; delivered and refunded have their own messages (ADR-061).
 */
const NOTIFYING: OrderStatus[] = ['PAID', 'SHIPPED', 'READY_FOR_PICKUP', 'DELIVERED', 'CANCELLED', 'REFUNDED'];

export function notifiesCustomer(target: OrderStatus): boolean {
  return NOTIFYING.includes(target);
}

/** Before it leaves: the address can still be corrected (ADR-059). */
export function canEditAddress(status: OrderStatus): boolean {
  return ['PENDING_PAYMENT', 'AWAITING_COLLECTION', 'PAID', 'PROCESSING', 'READY_FOR_PICKUP'].includes(status);
}

/**
 * The customer messages that are still true of this order — what "send
 * again" offers. Mirrors the API's `notificationApplies`; the API refuses the
 * rest regardless.
 */
export function resendable(order: {
  status: OrderStatus;
  paidAt: string | null;
  shipments: unknown[];
}): OrderNotificationType[] {
  const offered: OrderNotificationType[] = ['order.placed'];
  if (order.paidAt && order.status !== 'CANCELLED') offered.push('order.paid');
  if ((order.status === 'SHIPPED' || order.status === 'DELIVERED') && order.shipments.length > 0) {
    offered.push('order.shipped');
  }
  if (order.status === 'READY_FOR_PICKUP') offered.push('order.ready_for_pickup');
  if (order.status === 'CANCELLED') offered.push('order.cancelled');
  return offered;
}

/**
 * The moves a whole selection can make together: legal for every order in it,
 * and needing nothing order by order. Shipping wants each parcel's tracking,
 * and cancelling or a money move wants a reason someone can stand behind —
 * those stay one order at a time.
 */
export function bulkMoves(selection: { status: OrderStatus; isPickup: boolean }[]): OrderStatus[] {
  if (selection.length === 0) return [];
  const [first, ...rest] = selection.map((order) =>
    nextStatuses(order.status, order.isPickup).filter(
      (move) => !requiresTracking(move) && !requiresReason(move, order.status),
    ),
  );
  return (first ?? []).filter((move) => rest.every((moves) => moves.includes(move)));
}

/** One step of the order's road, for the progress line on its page. */
export interface ProgressStep {
  id: 'placed' | 'paid' | 'processing' | 'sent' | 'delivered';
  state: 'done' | 'current' | 'todo';
}

/**
 * Where the order is on its road — placed, paid, being prepared, sent (or
 * ready to collect), delivered (or collected). A cancelled or refunded order
 * has left the road and shows none.
 */
export function progressOf(order: { status: OrderStatus; requiresShipping?: boolean }): ProgressStep[] | null {
  if (order.status === 'CANCELLED' || order.status === 'REFUNDED') return null;

  const reached: Record<OrderStatus, number> = {
    PENDING_PAYMENT: 0,
    AWAITING_COLLECTION: 1,
    PAID: 1,
    PROCESSING: 2,
    SHIPPED: 3,
    READY_FOR_PICKUP: 3,
    DELIVERED: 4,
    CANCELLED: -1,
    REFUNDED: -1,
  };
  // Downloads and codes are delivered the moment they are paid (ADR-077): nothing is prepared or sent.
  if (order.requiresShipping === false) {
    const steps: ProgressStep['id'][] = ['placed', 'paid', 'delivered'];
    const done = order.status === 'DELIVERED' ? 2 : order.status === 'PENDING_PAYMENT' ? 0 : 1;
    return steps.map((id, index) => ({ id, state: index <= done ? 'done' : index === done + 1 ? 'current' : 'todo' }));
  }
  const at = reached[order.status];
  const ids: ProgressStep['id'][] = ['placed', 'paid', 'processing', 'sent', 'delivered'];

  // Done up to where it is; the step after that is what happens next.
  return ids.map((id, index) => ({
    id,
    state: index <= at ? 'done' : index === at + 1 ? 'current' : 'todo',
  }));
}
